import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import _ from 'lodash'
import { ChartType } from 'chart.js'
import { Trans, t } from '@lingui/macro'

import {
  TimeUnitEnum,
  TopicVersionType
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconDocEnrolment from '../../../components/common/icon/IconDocEnrolment'
import {
  AnalyticsHighlight,
  AnalyticsHighlightTextColorEnum
} from '../AnalyticsHighlight'
import AnalyticsChart from '../components/AnalyticsChart'
import { graphOptions } from '../chartOptions'
import { QuizAggregateResults } from '../../topic-detail-page/components/quizes/quizAggregateResults/QuizAggregateResults'
import { AnalyticsHeader } from '../AnalyticsHeader'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import { TableExtendedHeader } from '../../../components/common/table-extended-old/table-extended-header/TableExtendedHeader'
import { AnalyticsRating } from '../components/analytics-rating/AnalyticsRating'
import TabsLegacy from '../../../components/common/tabs/TabsLegacy'
import IconGraph from '../../../components/common/icon/IconGraph'
import IconCheck from '../../../components/common/icon/IconCheck'
import WorkbooksOverviewTable from '../workbooks/WorkbooksOverviewTable'
import { marbleColorForRating } from '../../../components/common/marble-rating/Marble'
import Loader from '../../../components/common/loader/Loader'
import { ResourceActivityOverviewTable } from '../resource-activity-table/ResourceActivityOverviewTable'
import ErrorMessage from '../../../components/common/error/ErrorMessage'
import { useResourceDetailAnalyticsQuery } from './ResourceDetailAnalytics.codegen'
import { SCORMQuizAggregateResults } from '../../topic-detail-page/components/quizes/quizAggregateResults/SCORMQuizAggregateResults'
import { AnalyticsContext } from '../AnalyticsContext'
import { useParams } from 'react-router'
import {
  StatusArchivedGrey,
  StatusDraftGrey
} from 'front-end/src/components/common/status/Status'
import { VersionOverviewTable } from '../resource-versions-table/VersionOverviewTable'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { AnalyticsFiltersEnum } from '../constants'
import { IconVersions } from 'front-end/src/components/common/icon/IconVersions'

const ResourceDetailAnalytics = () => {
  const history = useHistory()
  const { topicId: topicIdString } = useParams<{ topicId: string }>()
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const [chartTimeUnit, setChartTimeUnit] = useState<TimeUnitEnum>(
    TimeUnitEnum.DAY
  )
  const topicId = Number(topicIdString)
  const filterWithTopicId = {
    ..._.omit(filterAsQueryVariables),
    topicId
  }
  const { data, loading } = useResourceDetailAnalyticsQuery({
    fetchPolicy: 'cache-first',
    variables: {
      filter: filterWithTopicId,
      topicId,
      chartTimeUnit
    }
  })

  if (loading) {
    return <Loader />
  }
  if (!data?.topic) {
    // user is a STUDENT but in analytics, we probably show them even topics they don't have access to
    return (
      <ErrorMessage>
        <Trans>This resource could not be found</Trans>
      </ErrorMessage>
    )
  }

  const {
    analytics: {
      resources: {
        usersViewedResourceCount,
        totalViewsCount,
        topicViewsByTimeUnit,
        ratingSpread,
        averageRating
      },
      workbooks: { workbooksCount, completedCount, passedWorkbooksInPercents }
    },
    topic
  } = data

  const title = topic.currentTitle

  const getPathNameWithSearch = () => {
    return location.pathname + location.search
  }

  const tabList = [
    {
      caption: t`Activity`,
      paramName: 'activity',
      href: getPathNameWithSearch(),
      icon: <IconGraph />,
      getComponent: () => <ResourceActivityOverviewTable resourceId={topicId} />
    },
    {
      caption: t`Enrolments`,
      paramName: 'workbooks',
      href: getPathNameWithSearch(),
      icon: <IconDocEnrolment />,
      getComponent: () => (
        <WorkbooksOverviewTable resourceId={topicId} showTime />
      )
    },
    {
      caption: t`Quiz results`,
      paramName: 'quiz-results',
      href: getPathNameWithSearch(),
      icon: <IconCheck />,
      getComponent: () => (
        <>
          {topic.currentTopicVersion?.topic_type === TopicVersionType.scorm ? (
            <SCORMQuizAggregateResults
              topicId={topicId}
              title={topic.currentTitle}
            />
          ) : (
            <QuizAggregateResults
              interval={_.pick(filterAsQueryVariables, ['from', 'to'])}
              // @ts-ignore
              groupIds={filterAsQueryVariables.groupIds}
              topicId={topicId}
            />
          )}
        </>
      )
    },
    {
      caption: t`Versions`,
      paramName: 'versions',
      href: getPathNameWithSearch(),
      icon: <IconVersions />,
      getComponent: () => (
        <VersionOverviewTable
          productionVersionId={topic?.currentTopicVersion?.id}
          resourceId={topicId}
        />
      )
    }
  ]

  let workbooksPassRate
  let workbooksPassRateTextColor
  if (
    passedWorkbooksInPercents === null ||
    passedWorkbooksInPercents === undefined
  ) {
    workbooksPassRate = '--'
    workbooksPassRateTextColor = AnalyticsHighlightTextColorEnum.grey
  } else {
    workbooksPassRate = `${Math.round(passedWorkbooksInPercents)}%`
    workbooksPassRateTextColor = AnalyticsHighlightTextColorEnum.black
  }

  const renderAnalyticsBoxes = () => {
    return (
      <>
        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <div className="AnalyticsHighlight__row">
              <AnalyticsHighlight
                value={totalViewsCount}
                icon="eye"
                description={t`Resource views`}
                iconColor="blue"
              />
              <AnalyticsHighlight
                value={(totalViewsCount / usersViewedResourceCount).toFixed(1)}
                icon="check"
                description={t`Views per user`}
                iconColor="green"
              />
            </div>
            <AnalyticsChart
              width={800}
              height={160}
              config={{
                type: 'line' as ChartType,
                options: graphOptions
              }}
              data={[{ dataset: topicViewsByTimeUnit, color: '11,146,213' }]}
              granularity={chartTimeUnit}
              onGranularityChanged={setChartTimeUnit}
            />
          </BorderedBox>
        </div>

        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <TableExtendedHeader title={t`Ratings`} />

            <div className="AnalyticsRating__wrapper">
              <div className="AnalyticsRating__values">
                {[...ratingSpread].reverse().map((item) => (
                  <AnalyticsRating
                    key={item.rating}
                    color={marbleColorForRating(item.rating)}
                    total={Math.max.apply(
                      Math,
                      ratingSpread.map((o) => o.count)
                    )}
                    value={item.count}
                    order={item.rating}
                  />
                ))}
              </div>
              <AnalyticsHighlight
                value={averageRating}
                description={t`Avg. Rating`}
              />
            </div>
          </BorderedBox>

          <BorderedBox className="AnalyticsRow__box">
            <TableExtendedHeader title={t`Enrolments`} />
            <div className="AnalyticsHighlight__row">
              <AnalyticsHighlight
                value={workbooksCount} // TODO verify
                icon={<IconDocEnrolment boxSize="20px" />}
                description={t`Enrolments`}
              />
              <AnalyticsHighlight
                value={completedCount}
                icon="check"
                iconColor="green"
                description={t`Completed`}
              />
              <AnalyticsHighlight
                value={workbooksPassRate}
                icon="pie-chart"
                iconColor="blue"
                description={t`Pass rate`}
                textColor={workbooksPassRateTextColor}
              />
            </div>
          </BorderedBox>
        </div>
      </>
    )
  }

  return (
    <>
      <AnalyticsHeader
        showFilterPanel
        goBack={history.goBack}
        link={linkBuilder.topicUrl(topic?.id)}
        activeFilters={[
          AnalyticsFiltersEnum.INCLUDE_ADMINS,
          AnalyticsFiltersEnum.INCLUDE_ARCHIVED,
          AnalyticsFiltersEnum.GROUP_FILTER,
          AnalyticsFiltersEnum.DATE_FILTER
        ]}
      >
        <>
          {title}
          {topic.deleted_at && <StatusArchivedGrey mx="8px" />}
          {!topic.deleted_at && !topic?.currentTopicVersion && (
            <StatusDraftGrey mx="8px" />
          )}
        </>
      </AnalyticsHeader>

      {renderAnalyticsBoxes()}

      <div className="AnalyticsRow">
        <div className="AnalyticsRow__box AnalyticsRow__tabs">
          <TabsLegacy tabList={tabList} />
        </div>
      </div>
    </>
  )
}

export default ResourceDetailAnalytics
